import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/StoreContext';
import { Input, Select } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { generateId, formatDuration } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';

export const Step2Workflows = ({ editingWorkflow = null, onComplete = null }) => {
  const { state, addEntity, updateEntity } = useStore();
  const [currentTasks, setCurrentTasks] = useState([]);
  const [wfName, setWfName] = useState('');
  const [categoryId, setCategoryId] = useState('');

  useEffect(() => {
    if (editingWorkflow) {
      setWfName(editingWorkflow.name || '');
      setCategoryId(editingWorkflow.categoryId || '');
      setCurrentTasks(JSON.parse(JSON.stringify(editingWorkflow.tasks || [])));
    } else {
      setWfName('');
      setCategoryId('');
      setCurrentTasks([]);
    }
  }, [editingWorkflow]);

  const libraryTasks = Object.values(state.taskTemplates || {});
  // Show only workflow categories in the dropdown
  const workflowCategories = Object.values(state.categories || {}).filter(c => c.type === 'workflow');

  const addTask = (template) => {
    const newTask = { 
      ...JSON.parse(JSON.stringify(template)), 
      templateId: template.id, 
      id: generateId('ti'), 
      status: 'todo' 
    };
    setCurrentTasks([...currentTasks, newTask]);
  };

  const handleSave = () => {
    if (!wfName || currentTasks.length === 0) return;
    const workflowData = {
      id: editingWorkflow ? editingWorkflow.id : generateId('w'),
      name: wfName,
      categoryId,
      tasks: currentTasks
    };

    if (editingWorkflow) {
      updateEntity('workflowTemplates', workflowData);
    } else {
      addEntity('workflowTemplates', workflowData);
    }

    if (onComplete) {
      onComplete();
    } else {
      setWfName('');
      setCategoryId('');
      setCurrentTasks([]);
    }
  };

  const totalMinutes = currentTasks.reduce((sum, t) => sum + (t.durationMinutes || 0), 0);

  return (
    <div className="h-full flex gap-6">
      <div className="w-1/3 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col shadow-xl">
        <h2 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
          <SafeIcon name="CheckSquare" className="text-primary" /> Task Library
        </h2>
        <div className="flex-1 overflow-auto space-y-2 pr-2 custom-scrollbar">
          {libraryTasks.map(t => (
            <div 
              key={t.id} 
              onClick={() => addTask(t)}
              className="bg-base-900 p-3 rounded-lg border border-base-700 cursor-pointer hover:border-primary/50 transition-all flex justify-between items-center group"
            >
              <div>
                <div className="font-medium text-sm text-gray-200">{t.name}</div>
                <div className="text-[10px] text-gray-500 font-mono mt-1">{formatDuration(t.durationMinutes)}</div>
              </div>
              <SafeIcon name="Plus" className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col shadow-xl">
        <div className="flex items-end gap-4 mb-6">
          <div className="flex-1 grid grid-cols-2 gap-4">
            <Input 
              label="Workflow Title" 
              value={wfName} 
              onChange={e => setWfName(e.target.value)} 
              placeholder="e.g., Design Phase" 
            />
            <Select 
              label="Workflow Category" 
              value={categoryId} 
              onChange={e => setCategoryId(e.target.value)} 
              options={[
                { label: 'Select Workflow Category...', value: '' },
                ...workflowCategories.map(c => ({ label: c.name, value: c.id }))
              ]} 
            />
          </div>
          <div className="bg-base-900 px-4 py-2 rounded-lg border border-base-700 text-center min-w-[120px] h-[42px] flex flex-col justify-center">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest leading-none mb-1">Total Effort</div>
            <div className="text-sm font-mono font-bold text-accent leading-none">{formatDuration(totalMinutes)}</div>
          </div>
        </div>

        <div className="flex-1 bg-base-900/50 rounded-xl border-2 border-dashed border-base-700 p-4 overflow-auto custom-scrollbar">
          {currentTasks.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <SafeIcon name="Layers" className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">Build your sequence from tasks</p>
            </div>
          ) : (
            <div className="space-y-2">
              {currentTasks.map((t, idx) => (
                <div key={`${t.id}-${idx}`} className="flex items-center gap-4 bg-base-800 p-3 rounded-lg border border-base-600 group">
                  <div className="w-6 h-6 rounded-full bg-base-900 border border-primary/50 flex items-center justify-center text-[10px] font-bold text-primary">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-white">{t.name}</div>
                    <div className="text-[10px] text-gray-500">{formatDuration(t.durationMinutes)}</div>
                  </div>
                  <button 
                    onClick={() => setCurrentTasks(currentTasks.filter((_, i) => i !== idx))}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded transition-all"
                  >
                    <SafeIcon name="X" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => onComplete?.()}>Cancel</Button>
          <Button onClick={handleSave} disabled={!wfName || currentTasks.length === 0}>
            {editingWorkflow ? 'Update Workflow' : 'Save Workflow Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};